import { useEffect, useState } from 'react';
import { Alert, Box, MenuItem } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomAutoComplete,
  CustomTextField,
} from '../../../common/components';
import { useGenerateConsumptionFromOrder } from '../../../hooks/useConsumptions';
import { INITIAL_GENERATE_CONSUMPTION_STATE } from '../constants/consumptionsConstants';
import {
  buildGenerateConsumptionPayload,
  getConsumptionApiErrorMessage,
} from '../utils/consumptionsUtils';

const GenerateConsumptionDialog = ({
  open,
  onClose,
  order,
  warehouses = [],
  defaultWarehouseId = '',
  onSuccess,
}) => {
  const [formData, setFormData] = useState(INITIAL_GENERATE_CONSUMPTION_STATE);
  const [errorMessage, setErrorMessage] = useState('');
  const generateMutation = useGenerateConsumptionFromOrder();

  useEffect(() => {
    if (open) {
      setFormData({
        uuid_warehouse: defaultWarehouseId || order?.uuid_destination_warehouse || '',
        observations: '',
      });
      setErrorMessage('');
    }
  }, [open, order?.uuid, defaultWarehouseId, order?.uuid_destination_warehouse]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      const payload = buildGenerateConsumptionPayload(formData);
      const consumption = await generateMutation.mutateAsync({
        orderId: order.uuid,
        payload,
      });
      onSuccess?.(consumption);
      onClose();
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo generar el consumo')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer open={open} onClose={onClose} size="medium">
      <DialogTitle>Generar consumo</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <CustomTextField
          label="Orden técnica"
          name="orderNumber"
          value={order?.number || '-'}
          disabled
        />

        <CustomAutoComplete
          label="Depósito"
          name="uuid_warehouse"
          value={formData.uuid_warehouse}
          onChange={handleChange}
        >
          {warehouses.map((warehouse) => (
            <MenuItem key={warehouse.uuid} value={warehouse.uuid}>
              {warehouse.name || warehouse.code}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomTextField
          label="Observaciones"
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          multiline
          minRows={2}
        />

        <Box component="p" sx={{ m: 0, color: 'text.secondary', fontSize: 14 }}>
          Al generar el consumo se descontará el stock del depósito seleccionado.
          Los artículos y cantidades provienen del pedido técnico y no pueden editarse.
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel="Generar consumo"
        saveButtonProps={{ isLoading: generateMutation.isPending }}
      />
    </DialogContainer>
  );
};

export default GenerateConsumptionDialog;
